/**
 * 量化淘金 - 常量、类型与接口数据映射
 * courseNo: 5f0a51df3bf44351aa50b1df798c9836
 */

export const QUANT_GOLD_COURSE_NO = '5f0a51df3bf44351aa50b1df798c9836'

/** 查看往期可选历史交易日数量（与 indexPool.json historyDays 一致） */
export const QUANT_GOLD_HISTORY_DAYS = 30

const INDEX_POOL_ASSET_BASE = 'https://pcstatic.sylapp.cn/helpmanual/assets/indexPool/5f0a51df3bf44351aa50b1df798c9836'

/** indexPool.json 中量化淘金卡片配置（接口未返回时兜底） */
export const QUANT_GOLD_PRODUCT_CARD = {
  courseNo: QUANT_GOLD_COURSE_NO,
  poolName: '量化淘金',
  poolNameImg: `${INDEX_POOL_ASSET_BASE}/poolNameImg.png`,
  poolNameImgDark: `${INDEX_POOL_ASSET_BASE}/poolNameImgDark.png`,
  remark: '*数据来源于交易所和第三方数据库，筹码大师存在一定局限性，所涉及任何内容及操作仅供您参考和学习使用，不构成投资建议，不构成对任何金融产品的购买、抛售或持有，不作为您做出任何投资的依据，不承诺您获得投资收益，也不与您约定分享收益或分担损失，本产品不作任何担保。您应自主做出投资决策， 自行承担投资风险和损失。投资有风险，入市须谨慎。',
  tags: ['AI监控', '进场信号', '离场信号'],
  content: '指标简介指标简介指标简介指标简介指标简介指标简介指标简介指标简介指标简介',
  disclaimer: '该产品由九方智投投顾王宁(登记编号A0740622090011)主导开发。',
  poolIcon: `${INDEX_POOL_ASSET_BASE}/poolIcon.png`,
  historyDays: String(QUANT_GOLD_HISTORY_DAYS)
}

export type QuantGoldMainTab = 'newChose' | 'dataTrack' | 'hotCase' | 'beforeChose'
export type QuantGoldSubTab = 'entrySignal' | 'exitSignal'
export type QuantGoldPlateHeat = 'all' | 'top10' | 'top20' | 'top50'

export interface QuantGoldFilterParams {
  upSpaceStars: number[]
  plateHeat: QuantGoldPlateHeat
  onlyOptional?: boolean
  optionalSymbols?: string[]
}

export interface QuantGoldPoolItem {
  symbol: string
  name: string
  market: string
  lastPx: number
  pxChangRate: number
  price: number
  upSpaceStar?: number
  highPct: number
  plateName: string
  plateHeatRank: number
  cumRate: number
  sellTime?: string | null
  entryTime?: string | null
  selectTime?: number
  exitDate?: string | null
  amount?: number
  isOptional?: boolean
  ext?: {
    plateCode?: string
    plateName?: string
    refTime?: number
    star?: number
    plateHot?: number
    [key: string]: any
  }
}

export type QuantGoldStatPeriod = 2 | 3 | 4

/** 数据追踪 / 热门案例统计周期：2近3月 3近1年 4至今*/
export const QUANT_GOLD_STAT_PERIODS: { value: QuantGoldStatPeriod; label: string }[] = [
  { value: 2, label: '近3月' },
  { value: 3, label: '近1年' },
  { value: 4, label: '至今' }
]

/** 热门案例默认统计周期 */
export const QUANT_GOLD_HOT_CASE_PERIOD: QuantGoldStatPeriod = 2

/** 量化淘金 ladder/list 策略码 */
export const QUANT_GOLD_STRATEGY_CODE: Record<QuantGoldSubTab, string> = {
  entrySignal: 'quantgold_buy',
  exitSignal: 'quantgold_sell'
}

const PLATE_HEAT_TOP_MAP: Record<Exclude<QuantGoldPlateHeat, 'all'>, number> = {
  top10: 10,
  top20: 20,
  top50: 50
}

function formatQuantGoldDate(val: number | string): string {
  const n = Number(val)
  const ms = n < 1e12 ? n * 1000 : n
  const d = new Date(ms)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** 产品卡片兜底（与 indexPool.json 字段一致） */
export function getQuantGoldProductFallback() {
  return [{ ...QUANT_GOLD_PRODUCT_CARD }]
}

/** 接口日历 timeList → YYYY-MM-DD 数组（降序，最新在前，与四维猎手 historyDates 一致） */
export function parseQuantGoldTimeList(timeList: any): string[] {
  if (!timeList) return []
  const raw = Array.isArray(timeList) ? timeList : []
  const dates = raw.map((item: any) => {
    if (typeof item === 'string') return item.slice(0, 10)
    if (typeof item === 'number') return formatQuantGoldDate(item)
    if (item?.tradingDay != null) return formatQuantGoldDate(item.tradingDay)
    return ''
  }).filter(Boolean)
  return normalizeQuantGoldHistoryDates(dates)
}

/** 历史可选日期降序排列 */
export function normalizeQuantGoldHistoryDates(dates: string[]): string[] {
  return dates.filter(Boolean).slice().sort((a, b) => b.localeCompare(a))
}

/** 取最新可选历史日期 */
export function getLatestQuantGoldHistoryDate(dates: string[]): string {
  return normalizeQuantGoldHistoryDates(dates)[0] || ''
}

/** YYYY-MM-DD → 毫秒时间戳（与四维猎手 handleDateChange 一致） */
export function toQuantGoldTradingDayTimestamp(dateStr: string | { format?: (fmt: string) => string }): number {
  const normalized = typeof dateStr === 'string'
    ? dateStr.slice(0, 10)
    : (dateStr?.format ? dateStr.format('YYYY-MM-DD') : '')
  const parts = normalized.split('-').map(Number)
  if (parts.length !== 3 || parts.some(n => !Number.isFinite(n))) {
    const n = Number(dateStr)
    return Number.isFinite(n) ? n : 0
  }
  return new Date(parts[0], parts[1] - 1, parts[2]).getTime()
}

function formatQuantGoldRefTime(val: number | string | null | undefined): string | null {
  if (val === null || val === undefined || val === '' || val === 0 || val === '0') return null
  const n = Number(val)
  if (!Number.isFinite(n) || n <= 0) return String(val).slice(0, 10) || null
  return formatQuantGoldDate(n)
}

/** 标准化市场代码，便于 WebSocket 匹配 */
export function normalizeQuantGoldMarket(market: string, symbol?: string): string {
  let m = (market || '').toLowerCase()
  if (m === 'sse' || m === 'sha' || m === 'shetf' || m === '1') m = 'sh'
  else if (m === 'szse' || m === 'sza' || m === 'szetf' || m === '0') m = 'sz'
  else if (m === 'bse' || m === '2') m = 'bj'
  if (!m && symbol) {
    const code = String(symbol)
    if (code.startsWith('6') || code.startsWith('5')) m = 'sh'
    else if (code.startsWith('0') || code.startsWith('3') || code.startsWith('1')) m = 'sz'
    else if (code.startsWith('4') || code.startsWith('8')) m = 'bj'
  }
  return m || 'sz'
}

function resolveQuantGoldRefTime(item: any, ext: Record<string, any>): number | string | null | undefined {
  return item.refTime ?? ext.refTime ?? null
}

/** 量化淘金股池接口项 → 列表行 */
export function mapQuantGoldPoolItem(item: any, subTab: QuantGoldSubTab): QuantGoldPoolItem {
  const symbol = String(item.symbol || '').padStart(6, '0')
  let ext = item.ext
  if (typeof ext === 'string') {
    try {
      ext = ext ? JSON.parse(ext) : {}
    } catch {
      ext = {}
    }
  }
  ext = ext || {}
  const plateName = ext.plateName ?? item.plateName ?? item.plate?.name ?? ''

  return {
    symbol,
    name: item.name,
    market: normalizeQuantGoldMarket(item.market, symbol),
    lastPx: item.lastPx,
    pxChangRate: item.pxChangeRate ?? item.pxChangRate ?? 0,
    price: item.selectedPrice ?? item.price,
    upSpaceStar: ext.star ?? item.star ?? item.upSpaceStar,
    highPct: item.highPct,
    plateName,
    plateHeatRank: ext.plateHot ?? item.plateHot ?? 0,
    cumRate: item.periodPct ?? item.cumRate,
    // 进场信号 Tab：离场信号时间 ← refTime；离场信号 Tab 无此列
    sellTime: subTab === 'entrySignal'
      ? formatQuantGoldRefTime(resolveQuantGoldRefTime(item, ext))
      : undefined,
    // 离场信号 Tab：进场信号时间 ← refTime；进场信号 Tab 无此列
    entryTime: subTab === 'exitSignal'
      ? formatQuantGoldRefTime(resolveQuantGoldRefTime(item, ext))
      : undefined,
    amount: item.amount,
    ext
  }
}

/** 构建量化淘金股池请求参数 */
export function buildQuantGoldPoolQuery(options: {
  subTab: QuantGoldSubTab
  mainTab: QuantGoldMainTab
  pageNo: number
  pageSize: number
  onlyOptional: number
  sortField: string
  sortType: string
  filters: QuantGoldFilterParams
  historyDays: number
  historyDate?: string
  sortFieldMap: Record<string, string>
  includeTradingDay?: boolean
}) {
  const apiSortField = options.sortField
    ? (options.sortFieldMap[options.sortField] || options.sortField)
    : ''
  const queryData: Record<string, any> = {
    strategyCode: QUANT_GOLD_STRATEGY_CODE[options.subTab],
    pageNo: options.pageNo,
    pageSize: options.pageSize,
    onlyOptional: options.onlyOptional,
    days: options.historyDays
  }
  if (apiSortField) {
    queryData.sortField = apiSortField
    queryData.sortType = options.sortType || 'desc'
  }
  // 上涨空间筛选仅进场信号支持
  if (
    options.subTab === 'entrySignal' &&
    options.filters.upSpaceStars?.length
  ) {
    queryData.star = options.filters.upSpaceStars.join(',')
  }
  if (options.filters.plateHeat && options.filters.plateHeat !== 'all') {
    queryData.top = PLATE_HEAT_TOP_MAP[options.filters.plateHeat]
  }
  if (
    options.includeTradingDay !== false &&
    options.mainTab === 'beforeChose' &&
    options.historyDate
  ) {
    queryData.tradingDay = toQuantGoldTradingDayTimestamp(options.historyDate)
  }
  return queryData
}

/** 热门案例接口项 → 列表行（最新/涨幅/成交额由 WebSocket 推送） */
export function mapQuantGoldHotCaseItem(item: any) {
  const symbol = String(item.symbol || '').padStart(6, '0')
  return {
    symbol,
    name: item.name,
    market: normalizeQuantGoldMarket(item.market, symbol),
    price: item.selectPrice,
    selectTime: item.selectTime,
    highPct: item.rangeRate,
    exitDate: item.sellTime,
    lastPx: null as number | null,
    pxChangRate: null as number | null,
    amount: null as number | null
  }
}
